import type { Company, Offer } from '@domain/entities';
import type {
  AppUserRepository,
  ArticleRepository,
  CompanyRepository,
  OfferRepository,
  ReviewRepository,
} from '@domain/repositories';
import type { UseCase } from '../use-case';

export interface DashboardKpi {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface PendingItem {
  id: string;
  kind: 'company' | 'offer';
  name: string;
  subtitle: string;
  typeLabel: string;
}

export interface DashboardSnapshot {
  kpis: DashboardKpi[];
  pending: PendingItem[];
  topOffers: Offer[];
  /** Badges de navigation. */
  badges: { companies: number; offers: number; reviews: number };
}

/** Agrège les compteurs des 5 sources : un seul aller-retour pour la vue. */
export class GetDashboardSnapshot implements UseCase<void, DashboardSnapshot> {
  constructor(
    private readonly companies: CompanyRepository,
    private readonly offers: OfferRepository,
    private readonly articles: ArticleRepository,
    private readonly users: AppUserRepository,
    private readonly reviews: ReviewRepository,
  ) {}

  async execute(): Promise<DashboardSnapshot> {
    const [
      pendingCompanies,
      pendingOffers,
      activeOffers,
      publishedArticles,
      activeUsers,
      topOffers,
      flaggedReviews,
    ] = await Promise.all([
      this.companies.listByStatus('pending'),
      this.offers.listByStatus('pending'),
      this.offers.countByStatus('active'),
      this.articles.countByStatus('published'),
      this.users.countByStatus('active'),
      this.offers.topByClicks(5),
      this.reviews.countFlagged(),
    ]);

    const pending: PendingItem[] = [
      ...pendingCompanies.map((c: Company) => ({
        id: c.id,
        kind: 'company' as const,
        name: c.name,
        subtitle: `${c.type} · ${c.city}`,
        typeLabel: 'Compte entreprise',
      })),
      ...pendingOffers.map((o: Offer) => ({
        id: o.id,
        kind: 'offer' as const,
        name: o.title,
        subtitle: o.company,
        typeLabel: 'Offre',
      })),
    ].slice(0, 5);

    return {
      kpis: [
        { key: 'pendingCompanies', label: 'Comptes en attente', value: pendingCompanies.length, color: '#E0A552' },
        { key: 'pendingOffers', label: 'Offres en attente', value: pendingOffers.length, color: '#7FB3D5' },
        { key: 'activeOffers', label: 'Offres actives', value: activeOffers, color: '#5CA989' },
        { key: 'publishedArticles', label: 'Articles publiés', value: publishedArticles, color: '#D67F9E' },
        { key: 'activeUsers', label: 'Utilisatrices actives', value: activeUsers, color: '#33506B' },
      ],
      pending,
      topOffers,
      badges: {
        companies: pendingCompanies.length,
        offers: pendingOffers.length,
        reviews: flaggedReviews,
      },
    };
  }
}
